import { createBaseFields, addressSchema } from './baseSchema.js';
import { commonUserFields, validateCommonFields, commonProfileFields, validateProfileFields, commonLocationFields, validateLocationFields, idDocumentSchema } from './userSchema.js';



/**
 * Employer collection schema
 */
export const employerSchema = {
  ...createBaseFields('', ''),
  ...commonUserFields,
  ...commonProfileFields,
  ...commonLocationFields,
  idDocument: {
    storagePath: { _value: '', _optional: true, _rule: 'this is string', _immutable: true }
  },

  employerType: { _value: 'standalone', _enum: ['standalone', 'branch', 'organization'], _immutable: true },
  organizationId: { _value: '', _optional: true, _immutable: true },

  gstNumber: { _value: '', _optional: true, _rule: 'this == "" || this.matches("^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$")' },

  cin: { _value: '', _optional: true },
  totalEmployees: { _value: 0, _optional: true, _rule: 'this >= 0' },
  femaleEmployees: { _value: 0, _optional: true, _rule: 'this >= 0' },

  poc1: {
    name: { _value: '', _rule: 'this.size() > 0' },
    phone: { _value: '', _rule: 'this.size() > 0' },
    designation: { _value: '', _optional: true }
  },
  poc2: { _value: null, _optional: true, _rule: 'this is map' },
  poc3: { _value: null, _optional: true, _rule: 'this is map' },

  headOffice: { _value: null, _optional: true, _rule: 'this is map' },

  verificationStatus: { _value: 'pending', _enum: ['pending', 'verified', 'rejected'], _immutable: true, _optional: true, _rule: 'oldData != null || this == "pending"' },
  fcmToken: { _value: '', _optional: true },
  fcmTokenUpdatedAt: { _value: null, _rule: 'this is timestamp', _optional: true },
  notificationsEnabled: { _value: false, _optional: true },

  averageRating: { _value: 0.0, _optional: true, _rule: 'this >= 0 && this <= 5' },
  ratingCount: { _value: 0, _optional: true, _rule: 'this >= 0' },

  chargePH: { _value: 125, _rule: 'this >= 0', _optional: true },

  postingPrivilege: { _value: 'both', _enum: ['job_only', 'internship_only', 'both'], _optional: true },

  deactivatedByAdmin: { _value: false, _optional: true },
  adminDeactivationReason: { _value: '', _optional: true },
};

/**
 * Create an employer document
 * @param {string} uid - User ID
 * @param {string} displayName - Display name
 * @param {Object} address - Address object
 * @param {Object} employerLocation - Business location with lat, lng, addressFormatted
 * @returns {Object} Employer document
 */
export const createEmployerDocument = ({
  uid,
  displayName,
  address,
  employerLocation,
  phoneNumber,
  idStoragePath,
  invitedBy,
  inviteCodeUsed,
  profilePicture,
  email,
  cin,
  totalEmployees,
  femaleEmployees,
  poc1,
  poc2,
  poc3,
  headOffice,
  gstNumber,
  employerType = 'standalone',
  organizationId = '',
}) => {
  const baseFields = createBaseFields(uid, displayName);

  return {
    ...baseFields,
    address: {
      addressLine: address.addressLine || '',
      landmark: address.landmark || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      country: address.country || 'IN',
    },
    location: {
      lat: employerLocation.lat,
      lng: employerLocation.lng,
      addressFormatted: employerLocation.addressFormatted || '',
      ...(employerLocation.h3Index ? { h3Index: employerLocation.h3Index } : {}),
    },
    phoneNumber: phoneNumber || '',
    email: email || '',
    idDocument: {
      storagePath: idStoragePath || '',
    },
    invitedBy: invitedBy || null,
    inviteCodeUsed: inviteCodeUsed || null,
    profilePicture: (profilePicture && profilePicture.storagePath && profilePicture.url) ? {
      storagePath: profilePicture.storagePath,
      url: profilePicture.url,
    } : {},
    gstNumber: gstNumber || '',
    employerType: employerType || 'standalone',
    organizationId: organizationId || '',
    cin: cin || '',
    totalEmployees: totalEmployees ? Number(totalEmployees) : 0,
    femaleEmployees: femaleEmployees ? Number(femaleEmployees) : 0,
    poc1: {
      name: poc1?.name || '',
      phone: poc1?.phone || '',
      designation: poc1?.designation || '',
    },
    poc2: {
      name: poc2?.name || '',
      phone: poc2?.phone || '',
      designation: poc2?.designation || '',
    },
    poc3: {
      name: poc3?.name || '',
      phone: poc3?.phone || '',
      designation: poc3?.designation || '',
    },
    ...(headOffice && (
      headOffice.address?.addressLine ||
      headOffice.address?.landmark ||
      headOffice.address?.city ||
      headOffice.address?.state ||
      headOffice.address?.pincode ||
      headOffice.poc?.name ||
      headOffice.poc?.phone ||
      headOffice.poc?.designation
    ) ? {
      headOffice: {
        address: {
          addressLine: headOffice.address?.addressLine || '',
          landmark: headOffice.address?.landmark || '',
          city: headOffice.address?.city || '',
          state: headOffice.address?.state || '',
          pincode: headOffice.address?.pincode || '',
        },
        poc: {
          name: headOffice.poc?.name || '',
          phone: headOffice.poc?.phone || '',
          designation: headOffice.poc?.designation || '',
        }
      }
    } : {}),
  };
};

/**
 * Validate employer document
 * @param {Object} data - Employer data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateEmployerDocument = (data) => {
  const errors = [...validateCommonFields(data), ...validateProfileFields(data), ...validateLocationFields(data)];
  
  if (data.gstNumber && data.gstNumber.trim() !== '') {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gstRegex.test(data.gstNumber)) {
      errors.push('valid gstNumber format is required');
    }
  }

  if (!data.poc1?.name) errors.push('poc1.name is required');
  if (!data.poc1?.phone) errors.push('poc1.phone is required');

  if (data.employerType === 'branch' && !data.organizationId) {
    errors.push('organizationId is required for branch employers');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
